import { useEffect, useRef, useState } from 'react';

export interface ScrollRevealOptions extends IntersectionObserverInit {
  /** Reveal again when the element leaves the viewport */
  repeat?: boolean;
}

interface ScrollRevealReturn<T extends HTMLElement> {
  ref: React.RefObject<T>;
  isVisible: boolean;
}

export function useScrollReveal<T extends HTMLElement = HTMLElement>(
  options: ScrollRevealOptions = {}
): ScrollRevealReturn<T> {
  const { repeat = false, ...observerOptions } = options;
  const elementRef = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = elementRef.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (!repeat) {
            observer.unobserve(entry.target);
          }
        } else if (repeat) {
          setIsVisible(false);
        }
      });
    }, observerOptions);

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [repeat, observerOptions.root, observerOptions.rootMargin, observerOptions.threshold]);

  return {
    ref: elementRef,
    isVisible,
  };
}
